import React, { useEffect, useState } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonButton,
  IonList,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonLoading,
  IonAlert,
} from "@ionic/react";
import {
  logOutOutline,
  personOutline,
  mailOutline,
  trophyOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import api from "@/services/api";
import { normalizeLanguage, setStoredLanguage, useI18n } from "@/i18n";

const Profile: React.FC = () => {
  const { player, logout, updatePlayer } = useAuthStore();
  const history = useHistory();
  const { t } = useI18n();
  const [preferredLanguage, setPreferredLanguage] = useState<"en" | "de">(
    normalizeLanguage(player?.preferredLanguage)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (player?.preferredLanguage) setPreferredLanguage(player.preferredLanguage);
  }, [player?.preferredLanguage]);

  const handleLogout = () => { logout(); history.push("/login"); };

  const handleSaveLanguage = async () => {
    if (!player) return;
    setSaving(true); setError(""); setSuccess("");
    try {
      const response = await api.patch("/api/users/profile", { preferredLanguage });
      updatePlayer({ ...player, preferredLanguage: response.data?.preferredLanguage || preferredLanguage });
      setStoredLanguage(normalizeLanguage(response.data?.preferredLanguage));
      setSuccess(t("languageSaved"));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update language.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("profileTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: "16px" }}>
          {/* Player card */}
          <IonCard style={{ margin: "0 0 10px" }}>
            <IonCardContent style={{ padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "12px" }}>
                <div style={{
                  width: "48px", height: "48px", borderRadius: "50%",
                  background: "var(--ion-color-primary)", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "20px", fontWeight: 700,
                }}>
                  {(player?.displayName || player?.username || "U").charAt(0).toUpperCase()}
                </div>
                <div>
                  <strong style={{ fontSize: "16px" }}>{player?.displayName || player?.username}</strong>
                  <p style={{ margin: "2px 0 0", color: "var(--ion-color-medium)", fontSize: "13px" }}>{player?.email}</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <div className="stat-pill" style={{ flex: 1 }}>
                  <IonIcon icon={trophyOutline} style={{ fontSize: "18px", color: "var(--app-gold)", marginBottom: "2px" }} />
                  <strong>{player?.totalPoints || 0}</strong>
                  <span>{t("points")}</span>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Info list */}
          <IonCard style={{ margin: "0 0 10px" }}>
            <IonCardContent style={{ padding: 0 }}>
              <IonList lines="full" style={{ padding: 0 }}>
                <IonItem style={{ "--min-height": "44px" }}>
                  <IonIcon icon={personOutline} slot="start" style={{ fontSize: "18px" }} />
                  <IonLabel><p style={{ fontSize: "11px", color: "var(--ion-color-medium)" }}>{t("username")}</p><h3 style={{ fontSize: "14px" }}>{player?.username}</h3></IonLabel>
                </IonItem>
                <IonItem style={{ "--min-height": "44px" }}>
                  <IonIcon icon={mailOutline} slot="start" style={{ fontSize: "18px" }} />
                  <IonLabel><p style={{ fontSize: "11px", color: "var(--ion-color-medium)" }}>{t("email")}</p><h3 style={{ fontSize: "14px" }}>{player?.email}</h3></IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Language */}
          <IonCard style={{ margin: "0 0 10px" }}>
            <IonCardContent style={{ padding: "14px 16px" }}>
              <strong style={{ fontSize: "14px", marginBottom: "10px", display: "block" }}>{t("languagePreferences")}</strong>
              <IonSelect
                label={t("preferredLanguage")}
                labelPlacement="stacked"
                fill="outline"
                value={preferredLanguage}
                onIonChange={(e) => setPreferredLanguage(e.detail.value)}
                style={{ marginBottom: "12px", "--border-radius": "8px" } as React.CSSProperties}
              >
                <IonSelectOption value="en">{t("english")}</IonSelectOption>
                <IonSelectOption value="de">{t("german")}</IonSelectOption>
              </IonSelect>
              <IonButton expand="block" onClick={handleSaveLanguage} disabled={saving} className="save-btn" style={{ "--border-radius": "10px" }}>
                {saving ? t("savingLanguage") : t("saveLanguage")}
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* Logout */}
          <IonButton expand="block" fill="outline" color="danger" onClick={handleLogout} style={{ "--border-radius": "10px", marginTop: "8px" }}>
            <IonIcon icon={logOutOutline} slot="start" />
            {t("logout")}
          </IonButton>
        </div>

        <IonLoading isOpen={saving} message={t("savingLanguage")} />
        <IonAlert isOpen={!!error} onDidDismiss={() => setError("")} header={t("errorTitle")} message={error} buttons={[t("okButton")]} />
        <IonAlert isOpen={!!success} onDidDismiss={() => setSuccess("")} header={t("successTitle")} message={success || t("languageSaved")} buttons={[t("okButton")]} />
      </IonContent>
    </IonPage>
  );
};

export default Profile;
